import { useState, useEffect, useRef } from "react";
import { getJobStatus } from "services/jobs";

/**
 * Custom hook that polls for job status
 * @param {string} jobId - The job ID to poll for
 * @param {object} options - Polling options
 * @param {number} options.interval - Polling interval in milliseconds (default: 2000)
 * @param {number} options.timeout - Maximum polling duration in milliseconds (default: 30000)
 * @returns {object} { jobStatus, isPolling, error }
 */
const useJobPolling = (jobId, options = {}) => {
	const { interval = 2000, timeout = 30000 } = options;

	const [jobStatus, setJobStatus] = useState(null);
	const [isPolling, setIsPolling] = useState(false);
	const [error, setError] = useState(null);

	const intervalRef = useRef(null);
	const startTimeRef = useRef(null);

	useEffect(() => {
		// Reset state when jobId changes
		if (!jobId) {
			// Clear any existing polling
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
			setIsPolling(false);
			setJobStatus(null);
			setError(null);
			return;
		}

		// Initialize polling
		setIsPolling(true);
		setError(null);
		setJobStatus(null);
		startTimeRef.current = Date.now();

		const pollJobStatus = async () => {
			// Check if we've exceeded the timeout
			const elapsedTime = Date.now() - startTimeRef.current;
			if (elapsedTime >= timeout) {
				if (intervalRef.current) {
					clearInterval(intervalRef.current);
					intervalRef.current = null;
				}
				setIsPolling(false);
				setError("Job polling timed out after 30 seconds");
				return;
			}

			try {
				const response = await getJobStatus(jobId);

				if (response.status) {
					const jobData = response.data;
					setJobStatus(jobData);

					// Stop polling if job has completedAt timestamp
					// This means the background job is done (whether success or failure)
					const isCompleted = !!jobData.completedAt;

					if (isCompleted) {
						if (intervalRef.current) {
							clearInterval(intervalRef.current);
							intervalRef.current = null;
						}
						setIsPolling(false);
					}
				} else {
					// API returned an error
					if (intervalRef.current) {
						clearInterval(intervalRef.current);
						intervalRef.current = null;
					}
					setIsPolling(false);
					setError(response.data || "Failed to fetch job status");
				}
			} catch (err) {
				if (intervalRef.current) {
					clearInterval(intervalRef.current);
					intervalRef.current = null;
				}
				setIsPolling(false);
				setError(err?.message || "An error occurred while polling job status");
			}
		};

		// Poll immediately
		pollJobStatus();

		// Then poll at the specified interval
		intervalRef.current = setInterval(pollJobStatus, interval);

		// Cleanup on unmount or when jobId changes
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [jobId, interval, timeout]);

	return { jobStatus, isPolling, error };
};

export default useJobPolling;
